import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import api from "../../lib/api";
import {
  Database, Trash2, Plus, Loader2, AlertTriangle, CheckCircle2,
  Users, BookOpen, GraduationCap, Shield, FileText, Award,
  Star, Headphones, Calendar, BarChart3, RefreshCw, ToggleLeft, ToggleRight
} from "lucide-react";

const AREA_ICONS = {
  users: Users, courses: BookOpen, enrollments: GraduationCap,
  compliance: Shield, assessments: FileText, learning_paths: BarChart3,
  sessions: Calendar, certificates: Award, gamification: Star,
  support_tickets: Headphones,
};

export default function DemoDataPage() {
  const [areas, setAreas] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [seeding, setSeeding] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);
  const [wipeMode, setWipeMode] = useState("selected");
  const [confirmText, setConfirmText] = useState("");
  const [wipeAll, setWipeAll] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [areasRes, statsRes] = await Promise.all([
        api.get("/demo-data/areas"),
        api.get("/demo-data/stats"),
      ]);
      setAreas(areasRes.data.areas || []);
      setTenants(areasRes.data.tenants || []);
      setStats(statsRes.data);
      setSelectedAreas(areasRes.data.areas?.map(a => a.key) || []);
    } catch { toast.error("Failed to load demo data info"); }
    finally { setLoading(false); }
  };

  const toggleArea = (key) => {
    setSelectedAreas(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => setSelectedAreas(areas.map(a => a.key));
  const deselectAll = () => setSelectedAreas([]);

  const handleSeed = async () => {
    if (selectedAreas.length === 0) { toast.error("Select at least one area"); return; }
    setSeeding(true);
    try {
      const body = { areas: selectedAreas, tenant_id: selectedTenant === "all" ? null : selectedTenant };
      const res = await api.post("/demo-data/seed", body);
      toast.success("Demo data seeded successfully");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Seeding failed"); }
    finally { setSeeding(false); }
  };

  const handleWipe = async () => {
    setShowWipeConfirm(false);
    setConfirmText("");
    setWiping(true);
    try {
      const body = {
        tenant_id: selectedTenant === "all" ? null : selectedTenant,
        areas: wipeMode === "selected" ? selectedAreas : null,
        wipe_all: wipeAll,
      };
      const res = await api.post("/demo-data/wipe", body);
      const totalDeleted = Object.values(res.data.results || {}).reduce((sum, v) => sum + (typeof v === "object" ? Object.values(v).reduce((a, b) => a + b, 0) : v), 0);
      toast.success(`Wiped ${totalDeleted} records successfully`);
      setWipeAll(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Wipe failed"); }
    finally { setWiping(false); }
  };

  const formatCount = (v) => {
    if (typeof v === "object") return Object.values(v).reduce((a, b) => a + b, 0);
    return v;
  };

  if (loading) return (
    <AdminLayout variant="super_admin">
      <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout variant="super_admin">
      <div className="max-w-5xl mx-auto space-y-6" data-testid="demo-data-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><Database className="w-5 h-5" /> Demo Data Manager</h1>
            <p className="text-sm text-muted-foreground mt-1">Seed or remove demo data across the platform</p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={fetchData} data-testid="refresh-stats-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>

        {/* Current Data Stats */}
        {stats && (stats.total_demo_records > 0 || stats.total_all_records > 0) && (
          <Card className="border shadow-none border-amber-200 bg-amber-50/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {stats.total_all_records} total records ({stats.total_demo_records} demo-flagged)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.total_stats || {}).map(([key, val]) => val > 0 && (
                  <Badge key={key} variant="outline" className="text-[10px] bg-white border-amber-200">
                    {key.replace(/_/g, " ")}: {val}
                    {(stats.stats?.[key] || 0) > 0 && <span className="ml-1 text-amber-600">({stats.stats[key]} demo)</span>}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tenant Selector */}
        <Card className="border shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Target Tenant</label>
                <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                  <SelectTrigger className="h-9" data-testid="tenant-selector"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenants (Global)</SelectItem>
                    {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Button variant="outline" size="sm" className="text-xs h-9" onClick={selectAll} data-testid="select-all-btn">Select All</Button>
                <Button variant="outline" size="sm" className="text-xs h-9" onClick={deselectAll} data-testid="deselect-all-btn">Deselect All</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Area Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {areas.map(area => {
            const Icon = AREA_ICONS[area.key] || Database;
            const isSelected = selectedAreas.includes(area.key);
            const demoCount = stats?.stats?.[area.key] || 0;
            const totalCount = stats?.total_stats?.[area.key] || 0;
            return (
              <button
                key={area.key}
                onClick={() => toggleArea(area.key)}
                className={`relative rounded-xl border-2 p-4 text-left transition-all duration-150 ${
                  isSelected
                    ? "border-[#1e3a5f] bg-[#1e3a5f] text-white shadow-md"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:shadow-sm"
                }`}
                data-testid={`area-toggle-${area.key}`}
              >
                {isSelected && <CheckCircle2 className="absolute top-2.5 right-2.5 w-4 h-4 text-white" />}
                <Icon className={`w-5 h-5 mb-2 ${isSelected ? "text-white/80" : "text-slate-400"}`} />
                <p className={`text-xs font-semibold leading-tight ${isSelected ? "text-white" : "text-slate-700"}`}>{area.label}</p>
                <p className={`text-[10px] mt-0.5 leading-tight ${isSelected ? "text-white/70" : "text-slate-400"}`}>{area.description}</p>
                {totalCount > 0 && (
                  <Badge variant="outline" className={`text-[9px] mt-2 ${isSelected ? "bg-white/15 border-white/30 text-white" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                    {totalCount} total{formatCount(demoCount) > 0 ? ` (${formatCount(demoCount)} demo)` : ""}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            variant="success"
            className="flex-1 gap-2 h-11"
            onClick={handleSeed}
            disabled={seeding || selectedAreas.length === 0}
            data-testid="seed-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {seeding ? "Seeding..." : `Seed Demo Data (${selectedAreas.length} areas)`}
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2 h-11 border-red-200 text-red-700 hover:bg-red-50"
            onClick={() => { setWipeMode("selected"); setShowWipeConfirm(true); }}
            disabled={wiping || selectedAreas.length === 0}
            data-testid="wipe-selected-btn"
          >
            {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Wipe Selected ({selectedAreas.length})
          </Button>
          <Button
            variant="destructive"
            className="gap-2 h-11"
            onClick={() => { setWipeMode("all"); setShowWipeConfirm(true); }}
            disabled={wiping}
            data-testid="wipe-all-btn"
          >
            <Trash2 className="w-4 h-4" /> Wipe All
          </Button>
        </div>

        {/* Wipe Confirmation Dialog — HubSpot-style type-to-confirm */}
        <Dialog open={showWipeConfirm} onOpenChange={(v) => { setShowWipeConfirm(v); if (!v) { setConfirmText(""); setWipeAll(false); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Confirm Data Wipe</DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-4">
              <p className="text-sm text-slate-700">
                {wipeMode === "all"
                  ? `This will permanently remove ${wipeAll ? "ALL" : "demo-flagged"} data from the system.`
                  : `This will permanently remove ${wipeAll ? "ALL" : "demo-flagged"} data from ${selectedAreas.length} selected area(s).`
                }
              </p>
              {selectedTenant !== "all" && (
                <p className="text-sm text-slate-700">Scoped to tenant: <strong>{tenants.find(t => t.id === selectedTenant)?.name || selectedTenant}</strong></p>
              )}
              {/* Wipe All toggle */}
              <div
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${wipeAll ? "bg-red-50 border-red-300" : "bg-slate-50 border-slate-200"}`}
                onClick={() => setWipeAll(!wipeAll)}
                data-testid="wipe-all-toggle"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Include non-demo records</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {wipeAll
                        ? "Will delete ALL records including real data (super admin accounts are protected)"
                        : "Only records flagged as demo data will be removed"
                      }
                    </p>
                  </div>
                  {wipeAll
                    ? <ToggleRight className="w-8 h-8 text-red-600 flex-shrink-0" />
                    : <ToggleLeft className="w-8 h-8 text-slate-400 flex-shrink-0" />
                  }
                </div>
              </div>
              <div className={`p-3 rounded-lg border ${wipeAll ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                <p className={`text-xs font-medium ${wipeAll ? "text-red-700" : "text-amber-700"}`}>
                  {wipeAll
                    ? "DANGER: This will delete ALL records in the selected areas. Super admin accounts are protected."
                    : "Only records marked as demo data (is_demo=true) will be removed."
                  }
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Type <span className="font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded text-xs tracking-wide">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-200 text-sm font-medium tracking-wide focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-colors"
                  autoComplete="off"
                  spellCheck="false"
                  data-testid="confirm-text-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowWipeConfirm(false); setConfirmText(""); setWipeAll(false); }}>Cancel</Button>
              <Button variant="destructive" onClick={handleWipe} disabled={confirmText !== "DELETE"} data-testid="confirm-wipe-btn">
                {wipeAll ? "Yes, Wipe ALL Data" : "Yes, Wipe Demo Data"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
